import React, { useState, useEffect } from "react";
import api from "@/api";
import BaseModal from "./BaseModal";
import { formatDate } from "@/utils/timezone";
import { useAuth } from "@/contexts/AuthContext";

const SENTIMENT_COLORS = {
  bullish: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  bearish: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
  neutral: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
};

const CONVICTION_COLORS = {
  high: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
  medium: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  low: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
};

const ACTION_COLORS = {
  buy_now: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  wait_for_dip: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  sell: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
  hold: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  watching: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
};

const SIGNAL_STATUS_COLORS = {
  proposed: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  accepted: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  executed: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
  dismissed: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
  expired: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
};

const Badge = ({ children, colorClass }) => (
  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${colorClass}`}>
    {children}
  </span>
);

/** Returns a human-readable description of what the pipeline should produce. */
function getPipelineDecision(sentiment, actionType) {
  if (sentiment === "bullish" && actionType === "buy_now")
    return { outcome: "BUY Signal", color: "#38a169", explain: "bullish + buy_now → signal created immediately" };
  if (sentiment === "bearish" && actionType === "sell")
    return { outcome: "SELL Signal", color: "#e53e3e", explain: "bearish + sell → signal created immediately" };
  if (actionType === "hold")
    return { outcome: "No action", color: "#a0aec0", explain: "hold → no signal (already positioned)" };
  if (actionType === "wait_for_dip" && sentiment !== "neutral")
    return { outcome: "Alerts", color: "#3182ce", explain: `${sentiment} + wait_for_dip → price alerts created, signal fires when price hits entry` };
  if (actionType === "watching" && sentiment !== "neutral")
    return { outcome: "Alerts", color: "#3182ce", explain: `${sentiment} + watching → price alerts created` };
  return {
    outcome: "No action",
    color: "#a0aec0",
    explain: `${sentiment || "??"} + ${actionType || "??"} → no actionable outcome`,
  };
}

const SectionHeader = ({ children }) => (
  <div
    style={{
      fontSize: "0.75rem",
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      color: "#718096",
      marginBottom: "0.5rem",
    }}
  >
    {children}
  </div>
);

const IntelDetailModal = ({ intelId, onClose }) => {
  const { user } = useAuth();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [reanalyzeMessage, setReanalyzeMessage] = useState(null);

  const isAdmin = user?.is_admin;

  const fetchDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/intel/${intelId}`);
      setDetail(res.data);
    } catch (err) {
      setError("Failed to load intel detail.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (intelId) fetchDetail();
  }, [intelId]);

  const handleReanalyze = async () => {
    setReanalyzing(true);
    setReanalyzeMessage(null);
    try {
      await api.post(`/intel/${intelId}/reanalyze`);
      setReanalyzeMessage("Reanalysis complete.");
      await fetchDetail();
    } catch (err) {
      setReanalyzeMessage(
        err?.response?.data?.detail || "Reanalysis failed."
      );
    } finally {
      setReanalyzing(false);
    }
  };

  const header = (
    <div className="flex items-center justify-between p-6">
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          Intel Detail
        </h2>
        {detail?.tickers?.map((t) => (
          <span
            key={t}
            style={{
              background: "#4299e1",
              color: "white",
              padding: "0.125rem 0.5rem",
              borderRadius: "4px",
              fontSize: "0.75rem",
              fontWeight: "600",
            }}
          >
            {t}
          </span>
        ))}
        {detail?.platform && (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {detail.platform}
          </span>
        )}
      </div>
      <button
        onClick={onClose}
        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
      >
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );

  const footer = (
    <div className="flex items-center justify-between gap-3 p-6">
      <div className="flex items-center gap-3">
        {isAdmin && (
          <button
            onClick={handleReanalyze}
            disabled={reanalyzing || loading}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white"
            style={{
              background: reanalyzing ? "#a0aec0" : "#805ad5",
              cursor: reanalyzing ? "not-allowed" : "pointer",
              border: "none",
              transition: "background 0.2s",
            }}
            onMouseEnter={(e) => {
              if (!reanalyzing) e.currentTarget.style.background = "#6b46c1";
            }}
            onMouseLeave={(e) => {
              if (!reanalyzing) e.currentTarget.style.background = "#805ad5";
            }}
          >
            {reanalyzing ? "Reanalyzing..." : "Reanalyze"}
          </button>
        )}
        {reanalyzeMessage && (
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {reanalyzeMessage}
          </span>
        )}
      </div>
      <button
        onClick={onClose}
        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium transition-colors"
      >
        Close
      </button>
    </div>
  );

  return (
    <BaseModal header={header} footer={footer} onClose={onClose} maxWidth="max-w-2xl">
      {loading ? (
        <div className="p-8 text-center text-gray-500 dark:text-gray-400">
          Loading...
        </div>
      ) : error ? (
        <div className="p-8 text-center text-red-500">{error}</div>
      ) : detail ? (
        <div style={{ padding: "1.5rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

          {/* Intel metadata */}
          <div>
            {detail.author && (
              <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#4a5568", marginBottom: "0.25rem" }}>
                {detail.author}
              </div>
            )}
            {detail.thesis && (
              <div style={{ fontSize: "0.875rem", color: "#2d3748", lineHeight: "1.6" }}>
                {detail.thesis}
              </div>
            )}
            <div style={{ fontSize: "0.7rem", color: "#a0aec0", marginTop: "0.5rem" }}>
              {formatDate(detail.captured_at, "MMM d, yyyy h:mm a")}
            </div>
          </div>

          <hr className="border-gray-200 dark:border-gray-700" />

          {/* Analysis */}
          <div>
            <SectionHeader>Analysis</SectionHeader>
            {detail.analysis ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
                  {detail.analysis.sentiment && (
                    <Badge colorClass={SENTIMENT_COLORS[detail.analysis.sentiment] || SENTIMENT_COLORS.neutral}>
                      {detail.analysis.sentiment}
                    </Badge>
                  )}
                  {detail.analysis.conviction && (
                    <Badge colorClass={CONVICTION_COLORS[detail.analysis.conviction] || CONVICTION_COLORS.low}>
                      {detail.analysis.conviction} conviction
                    </Badge>
                  )}
                  {detail.analysis.action_type && (
                    <Badge colorClass={ACTION_COLORS[detail.analysis.action_type] || ACTION_COLORS.watching}>
                      {detail.analysis.action_type.replace(/_/g, " ")}
                    </Badge>
                  )}
                  {detail.analysis.time_horizon && (
                    <span style={{ fontSize: "0.75rem", color: "#718096" }}>
                      {detail.analysis.time_horizon} horizon
                    </span>
                  )}
                </div>

                {/* Pipeline decision explanation */}
                {(() => {
                  const decision = getPipelineDecision(detail.analysis.sentiment, detail.analysis.action_type);
                  return (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        fontSize: "0.75rem",
                        background: "#f7fafc",
                        border: "1px solid #e2e8f0",
                        borderRadius: "6px",
                        padding: "0.4rem 0.6rem",
                      }}
                    >
                      <span style={{ color: "#718096", fontWeight: "600" }}>Pipeline:</span>
                      <span style={{ color: decision.color, fontWeight: "700" }}>{decision.outcome}</span>
                      <span style={{ color: "#a0aec0" }}>—</span>
                      <span style={{ color: "#718096" }}>{decision.explain}</span>
                    </div>
                  );
                })()}

                {/* Ticker targets */}
                {detail.analysis.ticker_targets && Object.keys(detail.analysis.ticker_targets).length > 0 && (
                  <div>
                    <div style={{ fontSize: "0.75rem", color: "#718096", marginBottom: "0.375rem" }}>
                      Price targets
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.375rem" }}>
                      {Object.entries(detail.analysis.ticker_targets).map(([ticker, targets]) => (
                        <div
                          key={ticker}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.75rem",
                            fontSize: "0.8rem",
                            background: "var(--card-bg, #f7fafc)",
                            border: "1px solid #e2e8f0",
                            borderRadius: "6px",
                            padding: "0.5rem 0.75rem",
                            flexWrap: "wrap",
                          }}
                        >
                          <span style={{ fontWeight: "700", color: "#2d3748", minWidth: "3rem" }}>{ticker}</span>
                          {targets.entry != null && (
                            <span style={{ color: "#38a169" }}>entry ${targets.entry}</span>
                          )}
                          {targets.take_profit != null && (
                            <span style={{ color: "#3182ce" }}>tp ${targets.take_profit}</span>
                          )}
                          {targets.stop_loss != null && (
                            <span style={{ color: "#e53e3e" }}>sl ${targets.stop_loss}</span>
                          )}
                          {targets.price_at_analysis != null && (
                            <span style={{ color: "#718096" }}>@ ${targets.price_at_analysis}</span>
                          )}
                          {targets.entry == null && targets.take_profit == null && targets.stop_loss == null && (
                            <span style={{ color: "#a0aec0" }}>no price targets</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {detail.analysis.catalysts?.length > 0 && (
                  <div style={{ fontSize: "0.8rem", color: "#4a5568" }}>
                    <span style={{ fontWeight: "600", color: "#718096" }}>Catalysts: </span>
                    {detail.analysis.catalysts.join(", ")}
                  </div>
                )}
                {detail.analysis.technical_levels?.length > 0 && (
                  <div style={{ fontSize: "0.8rem", color: "#4a5568" }}>
                    <span style={{ fontWeight: "600", color: "#718096" }}>Levels: </span>
                    {detail.analysis.technical_levels.join(", ")}
                  </div>
                )}
                <div style={{ fontSize: "0.7rem", color: "#a0aec0" }}>
                  Analyzed {formatDate(detail.analysis.analyzed_at, "MMM d, yyyy h:mm a")}
                </div>
              </div>
            ) : (
              <div
                style={{
                  fontSize: "0.875rem",
                  color: "#a0aec0",
                  background: "#fffbeb",
                  border: "1px solid #f6e05e",
                  borderRadius: "6px",
                  padding: "0.75rem",
                }}
              >
                No analysis yet.{isAdmin && " Click Reanalyze to run it now."}
              </div>
            )}
          </div>

          <hr className="border-gray-200 dark:border-gray-700" />

          {/* Alerts */}
          <div>
            <SectionHeader>Alerts ({detail.alerts.length})</SectionHeader>
            {detail.alerts.length === 0 ? (
              <div style={{ fontSize: "0.875rem", color: "#a0aec0" }}>No alerts created.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.375rem" }}>
                {detail.alerts.map((al) => (
                  <div
                    key={al.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.625rem",
                      fontSize: "0.8rem",
                      padding: "0.5rem 0.75rem",
                      background: al.triggered_at ? "#f0fff4" : "var(--card-bg, #f7fafc)",
                      border: `1px solid ${al.triggered_at ? "#9ae6b4" : "#e2e8f0"}`,
                      borderRadius: "6px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span style={{ fontWeight: "700", minWidth: "3.5rem" }}>{al.ticker}</span>
                    <span style={{ color: al.alert_type === "PRICE_ABOVE" ? "#38a169" : "#e53e3e" }}>
                      {al.alert_type === "PRICE_ABOVE" ? "↑" : "↓"} ${al.target_price}
                    </span>
                    {al.alert_role && (
                      <span style={{ color: "#718096", fontStyle: "italic" }}>{al.alert_role}</span>
                    )}
                    <span
                      style={{
                        marginLeft: "auto",
                        color: al.triggered_at ? "#38a169" : al.is_active ? "#3182ce" : "#a0aec0",
                        fontWeight: "500",
                      }}
                    >
                      {al.triggered_at ? "triggered" : al.is_active ? "active" : "disabled"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <hr className="border-gray-200 dark:border-gray-700" />

          {/* Signals */}
          <div>
            <SectionHeader>Signals ({detail.signals.length})</SectionHeader>
            {detail.signals.length === 0 ? (
              <div style={{ fontSize: "0.875rem", color: "#a0aec0" }}>
                {detail.analysis?.action_type === "wait_for_dip"
                  ? "Waiting for entry alert to trigger."
                  : "No signals created."}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.375rem" }}>
                {detail.signals.map((s) => (
                  <div
                    key={s.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.625rem",
                      fontSize: "0.8rem",
                      padding: "0.5rem 0.75rem",
                      background: "var(--card-bg, #f7fafc)",
                      border: "1px solid #e2e8f0",
                      borderRadius: "6px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: "700",
                        color: s.signal_type === "BUY" ? "#38a169" : "#e53e3e",
                      }}
                    >
                      {s.signal_type}
                    </span>
                    <span style={{ fontWeight: "600" }}>{s.ticker}</span>
                    {s.entry_target != null && (
                      <span style={{ color: "#718096" }}>entry ${s.entry_target}</span>
                    )}
                    {s.confidence_score != null && (
                      <span style={{ color: "#718096" }}>confidence {s.confidence_score}</span>
                    )}
                    <Badge colorClass={SIGNAL_STATUS_COLORS[s.status] || SIGNAL_STATUS_COLORS.proposed}>
                      {s.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      ) : null}
    </BaseModal>
  );
};

export default IntelDetailModal;
